import { useCallback, useEffect, useMemo, useState } from "react";
import { Typography, useTheme } from "@mui/material";
import { IStorageService } from "../../../modules/storage/models/IStorageService";
import {
  BalanceRequest,
  BalanceList,
  BalanceListRows,
  BalanceRow,
  BalanceSummaryRow,
} from "../../../modules/storage/models/storage-dto";
import AppError from "../../../utils/appError";
import { convertToDefaultDate } from "../../../utils/dateFormatter";
import { formatCNPJ, getThousand } from "../../../utils";
import { isEqual } from "lodash";

const ROWS_PER_PAGE = 10;

interface Filters {
  cnpjs: string[];
  contractId: string;
  date: string;
  nfAndSeries: string;
}

const initialFilters: Filters = {
  cnpjs: [],
  contractId: "",
  date: "",
  nfAndSeries: "",
};

const initialList: BalanceList = {
  rows: [],
  count: 0,
};

export interface BalanceDeps {
  storageService: IStorageService;
  snackbar: (args: { message: string; variant: "success" | "error" }) => void;
}

export interface BalanceProps {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useBalanceStorage(
  { storageService, snackbar }: BalanceDeps,
  { isLoading, setIsLoading }: BalanceProps
) {
  const [tab, setTab] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [balanceList, setBalanceList] = useState<BalanceList>(initialList);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(initialFilters);

  const theme = useTheme();

  const fetchList = useCallback(
    async (newFilters = appliedFilters) => {
      try {
        setIsLoading(true);
        const { cnpjs, contractId, date, nfAndSeries } = newFilters;

        const newData: BalanceRequest = {
          cnpjs: cnpjs.length ? cnpjs.join(",") : undefined,
          nfAndSeries: nfAndSeries,
          date: date,
          contractId: contractId,
          page: page,
          limit: ROWS_PER_PAGE,
        };

        if (tab) {
          const { total, content } = await storageService.getStorageBalance(
            newData
          );

          setBalanceList({
            count: total,
            rows: content.map(formatBalanceList),
          });

          return;
        }

        const { total, content } =
          await storageService.getStorageBalanceSummary(newData);

        setBalanceList({
          count: total,
          rows: content.map(formatBalanceSummaryList),
        });
      } catch (error) {
        snackbar({
          message: (error as AppError).message,
          variant: "error",
        });

        setBalanceList(initialList);
      } finally {
        setIsLoading(false);
      }
    },
    [appliedFilters, page, tab]
  );

  const formatBalanceList = (data: BalanceRow): BalanceListRows => ({
    CNPJ: formatCNPJ(data.CNPJ),
    invoice: data.invoice,
    salesOrder: data.salesOrder || "-",
    nf: data.nf,
    series: data.series,
    value: (
      <Typography color={colorizeAndTranslateByStatus(data.type).color}>
        {`${data.type === "ENTRANCE" ? "+" : "-"}${getThousand(
          Number(data.value)
        )}`}
      </Typography>
    ),
    nfDate: data.nfDate ? convertToDefaultDate(data.nfDate) : "-",
    orderDate: data.orderDate ?? "-",
    type: colorizeAndTranslateByStatus(data.type).translate,
    remainingQuotaBalance: getThousand(Number(data.remainingQuotaBalance)),
  });

  const formatBalanceSummaryList = (
    data: BalanceSummaryRow
  ): BalanceListRows => ({
    customerCompany: data.customerCompany?.description || "-",
    CNPJ: formatCNPJ(data.customerCompany?.document ?? ""),
    product: data.product || "-",
    mod: data.mod || "-",
    quota: getThousand(Number(data.quota ?? 0)),
    finalQuota: getThousand(Number(data.finalQuota ?? 0)),
    added: getThousand(Number(data.additional ?? 0)),
    cut: getThousand(Number(data.cut ?? 0)),
    stated: getThousand(Number(data.stated ?? 0)),
    balance: getThousand(Number(data.balance ?? 0)),
  });

  const colorizeAndTranslateByStatus = (operation: string) => {
    const { success, error, shadesOfDark } = theme.palette;
    switch (operation) {
      case "ENTRANCE":
        return { color: success.light, translate: "Entrada" };
      case "RETRIEVAL":
        return { color: error.light, translate: "Saída" };
      default:
        return { color: shadesOfDark.dark, translate: "Desconhecido" };
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleTabChange = (newTab: number) => {
    setTab(newTab);
    setPage(1);
  };

  const handleApplyFilters = (newFilters: Filters) => {
    setPage(1);
    setAppliedFilters(newFilters);
    fetchList(newFilters);
  };

  const handleClearList = () => {
    setPage(1);
    setAppliedFilters(initialFilters);
    setBalanceList(initialList);
  };

  useEffect(() => {
    if (!appliedFilters.contractId) return;
    fetchList();
  }, [page, tab]);

  const hasAppliedFilters = useMemo(() => {
    return !isEqual(appliedFilters, initialFilters);
  }, [appliedFilters, initialFilters]);

  return {
    tab,
    page,
    hasAppliedFilters,

    balanceList,
    setBalanceList,
    appliedFilters,
    setAppliedFilters,

    handleApplyFilters,
    handlePageChange,
    handleTabChange,
    fetchList,
    handleClearList,
  };
}
